import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n, LANGS } from "../lib/i18n.jsx";
import { api, fileUrl } from "../lib/api";
import { brandPx } from "../lib/theme";
import { CompanySwitcher } from "./CompanySwitcher";
import { NotificationsBell } from "./NotificationsBell";
import { initOfflineSync, isOnline } from "../lib/offline";
import { connectRealtime, disconnectRealtime, onState } from "../lib/realtime";
import { toast } from "sonner";
import {
  HardHat, LayoutDashboard, Users, Building2, FileText, Clock, BarChart3,
  Boxes, Ruler, Shield, Network, Briefcase, Scale, FormInput, GitBranch, Warehouse, ClipboardList,
  Zap, ScrollText, Settings as SettingsIcon, LogOut, Menu, Smartphone, Rocket, Home, CalendarDays, UserCircle, WifiOff, Palette, FolderOpen, Target, FileDown,
} from "lucide-react";

export const NAV = [
  { to: "/dashboard", key: "dashboard", icon: LayoutDashboard, perm: null, hideWorker: true },
  { to: "/worker", key: "start_work", icon: Smartphone, perm: "reports.create", workerOnly: true },
  { to: "/worker/reports", key: "my_reports", icon: FileText, perm: "reports.create", workerOnly: true },
  { to: "/my-property", key: "my_property", icon: Warehouse, perm: null },
  { to: "/employees", key: "employees", icon: Users, perm: "employees.view", hideWorker: true },
  { to: "/projects", key: "projects", icon: Building2, perm: "projects.view", hideWorker: true },
  { to: "/reports", key: "reports", icon: FileText, perm: "reports.view", hideWorker: true },
  { to: "/worktime", key: "worktime", icon: Clock, perm: "worktime.view", hideWorker: true },
  { to: "/pdf", key: "pdf_reports", icon: FileDown, perm: "reports.view", hideWorker: true },
  { to: "/analytics", key: "analytics", icon: BarChart3, perm: "analytics.view" },
  { to: "/plan-fact", key: "plan_fact", icon: Target, perm: "analytics.view", hideWorker: true },
  { to: "/works", key: "works", icon: Boxes, perm: "works.view", hideWorker: true },
  { to: "/warehouse", key: "warehouse", icon: Warehouse, perm: "warehouse.view", hideWorker: true },
  { to: "/requests", key: "material_requests", icon: ClipboardList, perm: "warehouse.request.create" },
  { to: "/norms", key: "norms", icon: Ruler, perm: "norms.view" },
  { to: "/documents", key: "documents", icon: FolderOpen, perm: "documents.view", hideWorker: true },
  { to: "/roles", key: "roles", icon: Shield, perm: "roles.manage" },
  { to: "/positions", key: "positions", icon: Briefcase, perm: "positions.manage" },
  { to: "/units", key: "units", icon: Scale, perm: "units.manage" },
  { to: "/organization", key: "organization", icon: Network, perm: "org.manage" },
  { to: "/onboarding", key: "onboarding", icon: Rocket, perm: "settings.manage" },
  { to: "/forms", key: "forms", icon: FormInput, perm: "forms.manage" },
  { to: "/workflows", key: "workflows", icon: GitBranch, perm: "workflows.manage" },
  { to: "/automations", key: "automations", icon: Zap, perm: "automations.manage" },
  { to: "/audit", key: "audit", icon: ScrollText, perm: "audit.view" },
  { to: "/theme", key: "theme", icon: Palette, perm: "settings.manage" },
  { to: "/settings", key: "settings", icon: SettingsIcon, perm: "settings.manage" },
  { to: "/profile", key: "profile", icon: UserCircle, perm: null },
];

const WORKER_TABS = [
  { to: "/worker", key: "home", icon: Home },
  { to: "/worker/day", key: "my_day", icon: CalendarDays },
  { to: "/worker/reports", key: "reports", icon: FileText },
  { to: "/profile", key: "profile", icon: UserCircle },
];

export const isWorkerUser = (user) => !!(user?.effective?.["reports.create"] &&
  !user?.effective?.["reports.approve"] && !user?.effective?.["analytics.view"] &&
  !user?.effective?.["users.manage"] && !user?.effective?.["employees.create"]);

export default function Layout() {
  const { user, logout, can } = useAuth();
  const { t, tn, lang, setLang } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [online, setOnline] = useState(isOnline());
  const [live, setLive] = useState(false);

  useEffect(() => { connectRealtime(); const off = onState(setLive); return () => { off(); disconnectRealtime(); }; }, [user?.company_id]);

  const isWorker = isWorkerUser(user);
  const navCfg = user?.nav_config;

  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    const stop = initOfflineSync((n) => toast.success(`${t("synced")}: ${n}`));
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); stop(); };
  }, [t]);

  const items = NAV.filter((n) => {
    if (n.perm && !can(n.perm)) return false;
    if (n.workerOnly && !isWorker) return false;
    if (n.hideWorker && isWorker) return false;
    if (navCfg && navCfg.length && !navCfg.includes(n.key) && n.key !== "profile") return false;
    return true;
  });

  const logo = user?.company?.logo_file_id ? fileUrl(user.company.logo_file_id) : user?.company?.logo_url;
  const wordmark = user?.company?.app_wordmark_file_id ? fileUrl(user.company.app_wordmark_file_id) : null;
  const px = brandPx(user?.company?.brand_size);
  const isDefault = !logo;
  const Brand = ({ size = 28, className = "" }) => (
    <span className={`flex items-center gap-2 min-w-0 ${className}`} data-testid="brand-logo">
      <img src={isDefault ? "/brand/mark.png" : logo} alt="logo" className="w-auto object-contain shrink-0" style={{ height: px.logo, maxWidth: px.logo * 1.6 }} />
      {wordmark
        ? <img src={wordmark} alt={tn(user?.company?.app_name) || user?.company?.name || ""} data-testid="brand-wordmark" className="w-auto object-contain min-w-0" style={{ height: px.mark, maxWidth: 160 }} />
        : <span className="font-display font-black text-lg truncate text-[var(--amber)]">{tn(user?.company?.app_name) || user?.company?.name || "KONVIA"}</span>}
    </span>
  );

  const SideNav = (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 flex items-center gap-2 text-2xl border-b border-slate-800"><Brand /></div>
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {items.map((n) => {
          const active = loc.pathname === n.to;
          const Icon = n.icon;
          return (
            <button key={n.to} data-testid={`nav-${n.key}`} onClick={() => { nav(n.to); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                active ? "bg-[var(--amber)] text-white" : "text-slate-300 hover:bg-slate-800"}`}>
              <Icon size={18} /> <span className="truncate">{t(n.key)}</span>
            </button>
          );
        })}
      </nav>
      <div className="p-3 border-t border-slate-800">
        <div className="flex items-center gap-2 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-[var(--amber)] text-white flex items-center justify-center font-bold">{(user?.name || "?")[0]}</div>
          <div className="min-w-0 flex-1">
            <div className="text-white text-sm font-semibold truncate">{user?.name}</div>
            <div className="text-slate-500 text-xs truncate">{(user?.roles || []).map((r) => tn(r.name)).join(", ")}</div>
          </div>
        </div>
        <button data-testid="logout-btn" onClick={logout} className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800">
          <LogOut size={16} /> {t("logout")}
        </button>
      </div>
    </div>
  );

  const OfflineBar = !online && (
    <div data-testid="offline-bar" className="bg-slate-800 text-white text-xs px-3 py-1.5 flex items-center justify-center gap-2"><WifiOff size={14} /> {t("offline")} — {t("saved_offline")}</div>
  );

  if (isWorker) {
    return (
      <div className="min-h-screen flex flex-col bg-[var(--bg)]" data-testid="worker-shell">
        <header className="sticky top-0 z-20 bg-[var(--card)] border-b border-[var(--border)] px-4 min-h-14 flex items-center gap-3 pt-[env(safe-area-inset-top)]">
          <Brand size={24} className="text-xl" />
          <div className="ml-auto flex items-center gap-4">
            <span data-testid="live-indicator" title={live ? "Live" : t("offline")} className={`w-2 h-2 rounded-full ${live ? "bg-[var(--green)]" : "bg-slate-400"}`} />
            <select data-testid="lang-switcher" value={lang} onChange={(e) => setLang(e.target.value)} className="bg-[var(--bg)] border border-[var(--border)] rounded-md px-2 h-9 text-base">
              {LANGS.map((l) => <option key={l.code} value={l.code}>{l.flag}</option>)}
            </select>
            <NotificationsBell pathname={loc.pathname} />
          </div>
        </header>
        {OfflineBar}
        <main className="flex-1 p-4 pb-[calc(6rem+env(safe-area-inset-bottom))] w-full max-w-md mx-auto animate-fadeup">
          <Outlet key={user?.company_id} />
        </main>
        <nav data-testid="worker-tabbar" className="fixed bottom-0 inset-x-0 z-30 bg-[var(--card)] border-t border-[var(--border)] grid grid-cols-4" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
          {WORKER_TABS.map((tb) => {
            const active = loc.pathname === tb.to || (tb.to === "/profile" && loc.pathname === "/my-property");
            const Icon = tb.icon;
            return (
              <button key={tb.to} data-testid={`tab-${tb.key}`} onClick={() => nav(tb.to)}
                className={`flex flex-col items-center justify-center gap-0.5 h-16 text-[11px] font-semibold min-w-0 px-1 ${active ? "text-[var(--amber)]" : "text-[var(--text-3)]"}`}>
                <Icon size={22} /> <span className="truncate max-w-full">{t(tb.key)}</span>
              </button>
            );
          })}
        </nav>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[var(--bg)]">
      <aside className="hidden lg:flex w-64 bg-[var(--sidebar)] flex-col fixed inset-y-0 z-30">{SideNav}</aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-[var(--sidebar)] pt-[env(safe-area-inset-top)]">{SideNav}</aside>
        </div>
      )}
      <div className="flex-1 lg:ml-64 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 bg-[var(--card)] border-b border-[var(--border)] px-4 min-h-14 flex items-center gap-3 pt-[env(safe-area-inset-top)]">
          <button className="lg:hidden p-2 -m-2 min-w-11 min-h-11 flex items-center justify-center" onClick={() => setOpen(true)} data-testid="menu-toggle"><Menu /></button>
          <img src={isDefault ? "/brand/mark.png" : logo} alt="" className="h-8 w-auto max-w-[44px] object-contain lg:hidden" />
          <CompanySwitcher />
          <div className="ml-auto flex items-center gap-4">
            <span data-testid="live-indicator" title={live ? "Live" : t("offline")} className={`w-2 h-2 rounded-full ${live ? "bg-[var(--green)]" : "bg-slate-400"}`} />
            <select data-testid="lang-switcher" value={lang} onChange={(e) => setLang(e.target.value)} className="bg-[var(--bg)] border border-[var(--border)] rounded-md px-2 h-9 text-sm">
              {LANGS.map((l) => <option key={l.code} value={l.code}>{l.flag}</option>)}
            </select>
            <NotificationsBell pathname={loc.pathname} />
          </div>
        </header>
        {OfflineBar}
        <main className="flex-1 p-4 sm:p-6 max-w-[1400px] w-full mx-auto animate-fadeup">
          <Outlet key={user?.company_id} />
        </main>
      </div>
    </div>
  );
}
